import {
  Box,
  IconButton,
  Paper,
  styled,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';
import Icon from '../IconSvg/Icon';
import NoDataSelected from '../NoDataSelected/NoDataSelectedMUI';

/** Outer shell only — surface, positioning. Never owns padding. */
const Frame = styled(Paper)({
  position: 'relative',
  overflow: 'hidden',
  pointerEvents: 'auto',
});

const CompactBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.lelySpacing.LG,
  overflow: 'hidden',
}));

const ExpandedBody = styled(Box)(({ theme }) => ({
  paddingTop: theme.lelySpacing.LG,
  paddingLeft: theme.lelySpacing.LG,
  paddingRight: theme.lelySpacing.LG,
  overflowX: 'auto',
  overflowY: 'hidden',
}));

const ToggleButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.lelySpacing.SM,
  right: theme.lelySpacing.SM,
  backgroundColor: theme.palette.background.paper,
}));

export type FoldableDetailsSectionProps = {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** When false, shows `emptyContent` (defaults to NoDataSelected), regardless of `expanded`. */
  hasContent: boolean;
  children?: ReactNode;
  emptyContent?: ReactNode;
  'data-testid'?: string;
  sx?: SxProps<Theme>;
};

/**
 * Collapsible details panel shell: bordered surface, chevron toggle, and
 * empty state. Pass domain content as children when `hasContent` is true —
 * the same children render in both the collapsed and expanded body, so
 * content that needs to look different in each state (e.g. title-only vs
 * full detail rows) should switch on the `expanded` flag itself rather than
 * being swapped out here.
 */
function FoldableDetailsSection({
  expanded,
  onExpandedChange,
  hasContent,
  children,
  emptyContent,
  'data-testid': testId,
  sx,
}: FoldableDetailsSectionProps): JSX.Element {
  const showCompact = !hasContent || !expanded;

  return (
    <Frame elevation={0} data-testid={testId} sx={sx}>
      {showCompact ? (
        <CompactBody>
          {hasContent ? children : emptyContent ?? <NoDataSelected />}
        </CompactBody>
      ) : (
        <ExpandedBody>
          <Box sx={(theme) => ({ pb: theme.lelySpacing.LG })}>{children}</Box>
        </ExpandedBody>
      )}

      <ToggleButton
        aria-label={expanded ? 'Collapse details' : 'Expand details'}
        onClick={() => onExpandedChange(!expanded)}
        size="small"
      >
        <Icon
          name={expanded ? 'lely-icon-caret-up' : 'lely-icon-caret-down'}
          style={{ width: 24, height: 24 }}
        />
      </ToggleButton>
    </Frame>
  );
}

export default FoldableDetailsSection;
